"use client";

import { c as _c } from "react/compiler-runtime";
import { ApiDrop } from "@/generated/models/ApiDrop";
import { getWaveRoute } from "@/helpers/navigation.helpers";
import { Drop, ExtendedDrop } from "@/helpers/waves/drop.helpers";
import useIsMobileDevice from "@/hooks/isMobileDevice";
import { ActiveDropState } from "@/types/dropInteractionTypes";
import Link from "next/link";
import { memo, useCallback, useState } from "react";
import { DropInteractionParams, DropLocation } from "../Drop";
import WaveDropActions from "../WaveDropActions";
import WaveDropAuthorPfp from "../WaveDropAuthorPfp";
import WaveDropContent from "../WaveDropContent";
import WaveDropHeader from "../WaveDropHeader";
import WaveDropMetadata from "../WaveDropMetadata";
import WaveDropMobileMenu from "../WaveDropMobileMenu";
import WaveDropRatings from "../WaveDropRatings";
import WaveDropReactions from "../WaveDropReactions";
import WaveDropReply from "../WaveDropReply";
import WinnerDropBadge from "./WinnerDropBadge";
const getRankColorsByRank = (rank: number | null): {
  borderColor: string;
  textColor: string;
} => {
  const rankNumber = typeof rank === "string" ? parseInt(rank as string, 10) : rank;
  if (rankNumber === 1) {
    return {
      borderColor: "#fbbf24",
      textColor: "#E8D48A"
    };
  } else if (rankNumber === 2) {
    return {
      borderColor: "#C0C0C0",
      textColor: "#E8E8E8"
    };
  } else if (rankNumber === 3) {
    return {
      borderColor: "#CD7F32",
      textColor: "#CD7F32"
    };
  } else {
    return {
      borderColor: "#60606C",
      textColor: "#7F8A93"
    };
  }
};
const getDropStyles = (isActiveDrop: boolean, colors: {
  borderColor: string;
  textColor: string;
}) => {
  if (isActiveDrop) {
    return {
      boxShadow: "inset 1.5px 0 0 rgba(60,203,127,0.7)"
    };
  }
  return {
    boxShadow: `inset 1.5px 0 0 ${colors.borderColor}80,
                inset 0 1px 0 ${colors.borderColor}20,
                inset -1.5px 0 0 ${colors.borderColor}20,
                inset 0 -1.5px 0 ${colors.borderColor}20`
  };
};
interface DefautWinnerDropProps {
  readonly drop: ExtendedDrop;
  readonly previousDrop: Drop | null;
  readonly nextDrop: Drop | null;
  readonly showWaveInfo: boolean;
  readonly activeDrop: ActiveDropState | null;
  readonly showReplyAndQuote: boolean;
  readonly location: DropLocation;
  readonly dropViewDropId: string | null;
  readonly onReply: (param: DropInteractionParams) => void;
  readonly onQuote: (param: DropInteractionParams) => void;
  readonly onReplyClick: (serialNo: number) => void;
  readonly onQuoteClick: (drop: ApiDrop) => void;
  readonly onDropContentClick?: (drop: ExtendedDrop) => void;
  readonly parentContainerRef?: React.RefObject<HTMLElement | null>;
}
const DefaultWinnerDrop = t0 => {
  const $ = _c(104);
  const {
    drop,
    showWaveInfo,
    activeDrop,
    location,
    dropViewDropId,
    onReply,
    onQuote,
    onReplyClick,
    onQuoteClick,
    onDropContentClick,
    showReplyAndQuote,
    parentContainerRef
  } = t0;
  const [activePartIndex, setActivePartIndex] = useState(0);
  const [isSlideUp, setIsSlideUp] = useState(false);
  const [longPressTriggered, setLongPressTriggered] = useState(false);
  const isActiveDrop = activeDrop?.drop.id === drop.id;
  const isStorm = drop.parts.length > 1;
  const isMobile = useIsMobileDevice();
  const effectiveRank = drop.winning_context?.place ?? drop.rank;
  const decisionTime = drop.winning_context?.decision_time;
  let handleLongPress;
  let handleOnAddReaction;
  let handleOnQuote;
  let handleOnReply;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  if ($[0] !== activePartIndex || $[1] !== drop || $[2] !== effectiveRank || $[3] !== isActiveDrop || $[4] !== isMobile || $[5] !== location || $[6] !== onQuote || $[7] !== onReply) {
    const colors = getRankColorsByRank(effectiveRank);
    let t6;
    if ($[17] !== isMobile) {
      t6 = () => {
        if (!isMobile) {
          return;
        }
        setLongPressTriggered(true);
        setIsSlideUp(true);
      };
      $[17] = isMobile;
      $[18] = t6;
    } else {
      t6 = $[18];
    }
    handleLongPress = t6;
    let t7;
    if ($[19] !== activePartIndex || $[20] !== drop || $[21] !== onReply) {
      t7 = () => {
        setIsSlideUp(false);
        onReply({
          drop,
          partId: drop.parts[activePartIndex].part_id
        });
      };
      $[19] = activePartIndex;
      $[20] = drop;
      $[21] = onReply;
      $[22] = t7;
    } else {
      t7 = $[22];
    }
    handleOnReply = t7;
    let t8;
    if ($[23] !== activePartIndex || $[24] !== drop || $[25] !== onQuote) {
      t8 = () => {
        setIsSlideUp(false);
        onQuote({
          drop,
          partId: drop.parts[activePartIndex].part_id
        });
      };
      $[23] = activePartIndex;
      $[24] = drop;
      $[25] = onQuote;
      $[26] = t8;
    } else {
      t8 = $[26];
    }
    handleOnQuote = t8;
    let t9;
    if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = () => {
        setIsSlideUp(false);
      };
      $[27] = t9;
    } else {
      t9 = $[27];
    }
    handleOnAddReaction = t9;
    t5 = `tw-w-full ${location === DropLocation.WAVE ? "tw-px-4 tw-py-1" : ""}`;
    t4 = `tw-relative tw-w-full tw-flex tw-flex-col tw-px-4 tw-py-3 tw-rounded-lg tw-overflow-hidden tw-group
          ${isActiveDrop ? "tw-bg-[#3CCB7F]/10" : location === DropLocation.WAVE ? "tw-bg-iron-900/60" : "tw-bg-iron-950"}`;
    t1 = "1px solid transparent";
    t2 = "1.5px solid transparent";
    t3 = getDropStyles(isActiveDrop, colors);
    $[0] = activePartIndex;
    $[1] = drop;
    $[2] = effectiveRank;
    $[3] = isActiveDrop;
    $[4] = isMobile;
    $[5] = location;
    $[6] = onQuote;
    $[7] = onReply;
    $[8] = handleLongPress;
    $[9] = handleOnAddReaction;
    $[10] = handleOnQuote;
    $[11] = handleOnReply;
    $[12] = t1;
    $[13] = t2;
    $[14] = t3;
    $[15] = t4;
    $[16] = t5;
  } else {
    handleLongPress = $[8];
    handleOnAddReaction = $[9];
    handleOnQuote = $[10];
    handleOnReply = $[11];
    t1 = $[12];
    t2 = $[13];
    t3 = $[14];
    t4 = $[15];
    t5 = $[16];
  }
  let t6;
  if ($[28] !== t1 || $[29] !== t2 || $[30] !== t3) {
    t6 = {
      border: t1,
      borderLeft: t2,
      ...t3,
      transition: "box-shadow 0.2s ease, background-color 0.2s ease"
    };
    $[28] = t1;
    $[29] = t2;
    $[30] = t3;
    $[31] = t6;
  } else {
    t6 = $[31];
  }
  let t7;
  if ($[32] !== drop.reply_to || $[33] !== drop.wave || $[34] !== dropViewDropId || $[35] !== onReplyClick) {
    t7 = drop.reply_to && drop.reply_to.drop_id !== dropViewDropId && <WaveDropReply onReplyClick={onReplyClick} dropId={drop.reply_to.drop_id} dropPartId={drop.reply_to.drop_part_id} maybeDrop={drop.reply_to.drop ? {
      ...drop.reply_to.drop,
      wave: drop.wave
    } : null} />;
    $[32] = drop.reply_to;
    $[33] = drop.wave;
    $[34] = dropViewDropId;
    $[35] = onReplyClick;
    $[36] = t7;
  } else {
    t7 = $[36];
  }
  let t8;
  if ($[37] !== drop) {
    t8 = <WaveDropAuthorPfp drop={drop} />;
    $[37] = drop;
    $[38] = t8;
  } else {
    t8 = $[38];
  }
  let t9;
  if ($[39] !== activePartIndex || $[40] !== drop || $[41] !== isStorm) {
    t9 = <WaveDropHeader drop={drop} showWaveInfo={false} isStorm={isStorm} currentPartIndex={activePartIndex} partsCount={drop.parts.length} />;
    $[39] = activePartIndex;
    $[40] = drop;
    $[41] = isStorm;
    $[42] = t9;
  } else {
    t9 = $[42];
  }
  const t10 = decisionTime ?? null;
  let t11;
  if ($[43] !== effectiveRank || $[44] !== t10) {
    t11 = <WinnerDropBadge rank={effectiveRank} decisionTime={t10} />;
    $[43] = effectiveRank;
    $[44] = t10;
    $[45] = t11;
  } else {
    t11 = $[45];
  }
  let t12;
  if ($[46] !== drop.wave || $[47] !== showWaveInfo) {
    t12 = showWaveInfo && (() => {
      const waveDetails = drop.wave as unknown as {
        chat?: {
          scope?: {
            group?: {
              is_direct_message?: boolean;
            };
          };
        };
      } ?? undefined;
      const isDirectMessage = waveDetails?.chat?.scope?.group?.is_direct_message ?? false;
      const waveHref = getWaveRoute({
        waveId: drop.wave.id,
        isDirectMessage,
        isApp: false
      });
      return <Link href={waveHref} onClick={_temp} className="tw-text-xs tw-leading-none tw-mt-0.5 tw-text-iron-500 hover:tw-text-iron-300 tw-transition tw-duration-300 tw-ease-out tw-no-underline">{drop.wave.name}</Link>;
    })();
    $[46] = drop.wave;
    $[47] = showWaveInfo;
    $[48] = t12;
  } else {
    t12 = $[48];
  }
  let t13;
  if ($[49] !== t11 || $[50] !== t12 || $[51] !== t9) {
    t13 = <div className="tw-flex tw-flex-col tw-items-start tw-gap-y-1">{t9}{t11}{t12}</div>;
    $[49] = t11;
    $[50] = t12;
    $[51] = t9;
    $[52] = t13;
  } else {
    t13 = $[52];
  }
  let t14;
  if ($[53] !== activePartIndex || $[54] !== drop || $[55] !== handleLongPress || $[56] !== onDropContentClick || $[57] !== onQuoteClick || $[58] !== parentContainerRef) {
    t14 = <div><WaveDropContent drop={drop} activePartIndex={activePartIndex} setActivePartIndex={setActivePartIndex} onDropContentClick={onDropContentClick} onQuoteClick={onQuoteClick} parentContainerRef={parentContainerRef} onLongPress={handleLongPress} setLongPressTriggered={setLongPressTriggered} isCompetitionDrop={true} /></div>;
    $[53] = activePartIndex;
    $[54] = drop;
    $[55] = handleLongPress;
    $[56] = onDropContentClick;
    $[57] = onQuoteClick;
    $[58] = parentContainerRef;
    $[59] = t14;
  } else {
    t14 = $[59];
  }
  let t15;
  if ($[60] !== t13 || $[61] !== t14) {
    t15 = <div className="tw-flex tw-flex-col tw-w-full tw-gap-y-2">{t13}{t14}</div>;
    $[60] = t13;
    $[61] = t14;
    $[62] = t15;
  } else {
    t15 = $[62];
  }
  let t16;
  if ($[63] !== t15 || $[64] !== t8) {
    t16 = <div className="tw-flex tw-gap-x-3 tw-w-full tw-text-left tw-bg-transparent tw-border-0 tw-relative tw-z-10">{t8}{t15}</div>;
    $[63] = t15;
    $[64] = t8;
    $[65] = t16;
  } else {
    t16 = $[65];
  }
  let t17;
  if ($[66] !== activePartIndex || $[67] !== drop || $[68] !== handleOnQuote || $[69] !== handleOnReply || $[70] !== isMobile || $[71] !== showReplyAndQuote) {
    t17 = !isMobile && showReplyAndQuote && <div className="tw-absolute tw-right-0 tw-top-1"><WaveDropActions drop={drop} activePartIndex={activePartIndex} onReply={handleOnReply} onQuote={handleOnQuote} /></div>;
    $[66] = activePartIndex;
    $[67] = drop;
    $[68] = handleOnQuote;
    $[69] = handleOnReply;
    $[70] = isMobile;
    $[71] = showReplyAndQuote;
    $[72] = t17;
  } else {
    t17 = $[72];
  }
  let t18;
  if ($[73] !== drop.metadata) {
    t18 = drop.metadata.length > 0 && <WaveDropMetadata metadata={drop.metadata} />;
    $[73] = drop.metadata;
    $[74] = t18;
  } else {
    t18 = $[74];
  }
  let t19;
  if ($[75] !== drop) {
    t19 = !!drop.raters_count && <WaveDropRatings drop={drop} />;
    $[75] = drop;
    $[76] = t19;
  } else {
    t19 = $[76];
  }
  let t20;
  if ($[77] !== drop) {
    t20 = <WaveDropReactions drop={drop} />;
    $[77] = drop;
    $[78] = t20;
  } else {
    t20 = $[78];
  }
  let t21;
  if ($[79] !== t19 || $[80] !== t20) {
    t21 = <div className="tw-flex tw-w-full tw-items-center tw-gap-x-2 tw-gap-y-1 tw-flex-wrap">{t19}{t20}</div>;
    $[79] = t19;
    $[80] = t20;
    $[81] = t21;
  } else {
    t21 = $[81];
  }
  let t22;
  if ($[82] !== t18 || $[83] !== t21) {
    t22 = <div className="tw-flex tw-flex-col tw-gap-2 tw-ml-[3.5rem] tw-mt-1.5">{t18}{t21}</div>;
    $[82] = t18;
    $[83] = t21;
    $[84] = t22;
  } else {
    t22 = $[84];
  }
  let t23;
  if ($[85] !== t16 || $[86] !== t17 || $[87] !== t22 || $[88] !== t4 || $[89] !== t6 || $[90] !== t7) {
    t23 = <div className={t4} style={t6}>{t7}{t16}{t17}{t22}</div>;
    $[85] = t16;
    $[86] = t17;
    $[87] = t22;
    $[88] = t4;
    $[89] = t6;
    $[90] = t7;
    $[91] = t23;
  } else {
    t23 = $[91];
  }
  let t24;
  if ($[92] !== drop || $[93] !== handleOnAddReaction || $[94] !== handleOnQuote || $[95] !== handleOnReply || $[96] !== isSlideUp || $[97] !== longPressTriggered || $[98] !== showReplyAndQuote) {
    t24 = <WaveDropMobileMenu drop={drop} isOpen={isSlideUp} longPressTriggered={longPressTriggered} showReplyAndQuote={showReplyAndQuote} setOpen={setIsSlideUp} onReply={handleOnReply} onQuote={handleOnQuote} onAddReaction={handleOnAddReaction} />;
    $[92] = drop;
    $[93] = handleOnAddReaction;
    $[94] = handleOnQuote;
    $[95] = handleOnReply;
    $[96] = isSlideUp;
    $[97] = longPressTriggered;
    $[98] = showReplyAndQuote;
    $[99] = t24;
  } else {
    t24 = $[99];
  }
  let t25;
  if ($[100] !== t23 || $[101] !== t24 || $[102] !== t5) {
    t25 = <div className={t5}>{t23}{t24}</div>;
    $[100] = t23;
    $[101] = t24;
    $[102] = t5;
    $[103] = t25;
  } else {
    t25 = $[103];
  }
  return t25;
};
export default memo(DefaultWinnerDrop);
function _temp(e) {
  return e.stopPropagation();
}